import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Image, Hash, BookMarked, GraduationCap, Plus, Check, RefreshCw, Save, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { LensCapture } from "@/components/LensCapture";
import { useI18n } from "@/context/I18nContext";

const TABS = [
  { k: "describe", labelKey: "lens.describe", icon: Image },
  { k: "identify", labelKey: "lens.identifyCount", icon: Hash },
  { k: "vocab", labelKey: "lens.learnVocab", icon: BookMarked },
  { k: "quiz", labelKey: "lens.quizMe", icon: GraduationCap },
];

export default function SceneLens() {
  const { t } = useI18n();
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("describe");
  const [cache, setCache] = useState({});          // mode -> result
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState({});
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});

  const onCapture = async (b64) => {
    setImg(b64); setCache({}); setSaved({}); setAnswers({}); setRevealed({});
    setTab("describe");
    run("describe", b64);
  };

  const run = async (mode, b64) => {
    const image = b64 || img;
    if (!image || cache[mode]) return;
    setBusy(true);
    try {
      const r = await api.post("/lens/scene/analyze", { image, mode });
      setCache((c) => ({ ...c, [mode]: r.data }));
    } catch (e) {
      if (e?.response?.status === 402) toast.error("FluentLens is a premium feature.");
      else toast.error("Scene analysis failed — try again.");
    } finally { setBusy(false); }
  };

  const pick = (mode) => { setTab(mode); run(mode); };

  const addVocab = async (items) => {
    const fresh = items.filter((v) => v.word && !saved[v.word]);
    if (!fresh.length) return;
    try {
      await api.post("/vocab/add", { items: fresh.map((v) => ({ word: v.word, translation: v.translation, example_sentence: v.example || "" })), source: "scene_lens" });
      setSaved((s) => { const n = { ...s }; fresh.forEach((v) => { n[v.word] = true; }); return n; });
      toast.success(`Added ${fresh.length} to your Vocab Bank`);
    } catch { toast.error("Couldn't add to Vocab Bank."); }
  };

  const allVocab = () => (cache.vocab?.groups || []).flatMap((g) => g.words || []);

  const saveScan = async () => {
    try {
      await api.post("/lens/scans", { type: "scene", scene_summary: cache.describe?.description || "", study_questions: cache.quiz?.questions || null, saved_vocab: Object.keys(saved) });
      toast.success("Saved to FluentLens Archive");
    } catch { toast.error("Couldn't save scan."); }
  };

  const d = cache[tab];
  return (
    <AppLayout>
      <Seo title="Scene Lens · FluentLens" />
      <div className="max-w-3xl mx-auto" data-testid="scene-lens">
        <Link to="/lens" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-3"><ArrowLeft className="w-4 h-4" /> FluentLens</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mb-1">{t("lens.sceneLens")}</h1>
        <p className="text-gray-600 text-sm mb-5">{t("lensDesc.subScene")}</p>

        {!img && <LensCapture onCapture={onCapture} loading={loading} hint={t("lensDesc.hintScene")} />}

        {img && (
          <div className="space-y-4">
            <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="scene-tabs">
              {TABS.map((tb) => (
                <button key={tb.k} onClick={() => pick(tb.k)} data-testid={`scene-tab-${tb.k}`}
                  className={`nb-tag whitespace-nowrap flex items-center gap-1 ${tab === tb.k ? "bg-ink text-white" : "bg-white"}`}>
                  <tb.icon className="w-3.5 h-3.5" /> {t(tb.labelKey)}
                </button>
              ))}
              <button onClick={() => { setImg(null); }} className="nb-tag bg-white whitespace-nowrap" data-testid="scene-new">{t("lens.scanAgain")}</button>
            </div>

            {busy && !d && <div className="nb-card p-10 text-center"><RefreshCw className="w-7 h-7 animate-spin mx-auto" /><p className="font-cabinet font-bold mt-2">{t("common.loading")}</p></div>}

            {/* Describe */}
            {tab === "describe" && d && (
              <div className="nb-card bg-mint/40 p-4" data-testid="scene-describe">
                {d.scene_type && <span className="nb-tag bg-white text-xs capitalize">{d.scene_type}</span>}
                <p className="mt-2 font-medium" dir="auto">{d.description}</p>
                {d.description_translation && <p className="text-sm text-gray-600 mt-1" dir="auto">{d.description_translation}</p>}
                {(d.expressions || []).length > 0 && (
                  <ul className="mt-3 space-y-1 text-sm">{d.expressions.map((e, i) => <li key={`${e.phrase}-${i}`}><b dir="auto">{e.phrase}</b> — <span className="text-gray-600" dir="auto">{e.translation}</span></li>)}</ul>
                )}
              </div>
            )}

            {/* Identify & Count */}
            {tab === "identify" && d && (
              <div className="nb-card p-4 space-y-3" data-testid="scene-identify">
                {d.note && <p className="text-xs text-gray-500">{d.note}</p>}
                <div className="flex flex-wrap gap-2">
                  {(d.items || []).map((it, i) => (
                    <span key={`${it.target}-${i}`} className="nb-tag bg-peach/60 text-sm">{it.count_text} · <b dir="auto">{it.target}</b> <span dir="auto">({it.native})</span></span>
                  ))}
                </div>
                {(d.spatial || []).length > 0 && (
                  <div className="bg-lavender/30 border-2 border-ink rounded-xl p-3">
                    <div className="font-bold text-sm mb-1">{t("label.whereThings")}</div>
                    <ul className="space-y-1 text-sm">{d.spatial.map((s, i) => <li key={i}><b dir="auto">{s.target}</b> — <span className="text-gray-600" dir="auto">{s.native}</span></li>)}</ul>
                  </div>
                )}
              </div>
            )}

            {/* Learn Vocabulary */}
            {tab === "vocab" && d && (
              <div className="space-y-3" data-testid="scene-vocab">
                <div className="flex justify-end"><button onClick={() => addVocab(allVocab())} className="text-xs font-bold text-coral" data-testid="scene-add-all">{t("common.addAll")}</button></div>
                {(d.groups || []).map((g, gi) => (
                  <div key={`${g.group}-${gi}`} className="nb-card p-4">
                    <div className="font-cabinet font-bold mb-2">{g.group}</div>
                    <ul className="space-y-1 text-sm">
                      {(g.words || []).map((v, i) => (
                        <li key={`${v.word}-${i}`} className="flex items-center justify-between gap-2">
                          <span><b dir="auto">{v.article ? `${v.article} ` : ""}{v.word}</b> — <span className="text-gray-600" dir="auto">{v.translation}</span>{v.plural ? <span className="text-gray-400" dir="auto"> · pl. {v.plural}</span> : ""}</span>
                          {saved[v.word] ? <span className="text-xs text-gray-400 flex items-center gap-0.5"><Check className="w-3 h-3" /> {t("common.added")}</span>
                            : <button onClick={() => addVocab([v])} className="text-xs font-bold text-coral shrink-0" data-testid={`scene-add-${gi}-${i}`}><Plus className="w-3 h-3 inline" /> {t("common.add")}</button>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Quiz Me */}
            {tab === "quiz" && d && (
              <div className="space-y-3" data-testid="scene-quiz">
                {(d.questions || []).map((q, qi) => {
                  const opts = q.options || [];
                  const done = revealed[q.id || qi];
                  return (
                    <div key={q.id || qi} className="nb-card p-4" data-testid={`scene-q-${qi}`}>
                      <div className="flex items-center justify-between mb-2"><span className="nb-tag bg-lavender text-xs">{(q.type || "question").replace(/_/g, " ")}</span></div>
                      <p className="font-bold" dir="auto">{q.prompt}</p>
                      {opts.length > 0 ? (
                        <div className="grid gap-2 mt-3">{opts.map((o, oi) => <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [q.id || qi]: o }))} disabled={done} className={`text-left border-2 border-ink rounded-xl px-3 py-2 text-sm ${answers[q.id || qi] === o ? "bg-butter" : "bg-white"}`} data-testid={`scene-opt-${qi}-${oi}`} dir="auto">{o}</button>)}</div>
                      ) : (
                        <textarea value={answers[q.id || qi] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id || qi]: e.target.value }))} disabled={done} placeholder="Your answer…" className="nb-input w-full mt-3 h-16" data-testid={`scene-input-${qi}`} />
                      )}
                      {!done ? <button onClick={() => setRevealed((r) => ({ ...r, [q.id || qi]: true }))} className="nb-btn-primary py-2 px-4 text-sm mt-3" data-testid={`scene-check-${qi}`}>Check</button>
                        : <div className="mt-3 border-2 border-ink rounded-xl p-3 text-sm bg-mint/30" data-testid={`scene-feedback-${qi}`}>{q.answer && <div><b>Suggested answer:</b> <span dir="auto">{q.answer}</span></div>}{q.explanation && <div className="text-gray-600 mt-1" dir="auto">{q.explanation}</div>}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-gray-400 text-center">Want to focus on one item instead? <Link to="/lens/object" className="text-coral font-bold">Try Object Lens</Link>.</p>
            <button onClick={saveScan} className="nb-btn-outline w-full py-3" data-testid="scene-save-scan"><Save className="w-4 h-4" /> {t("lens.saveScan")}</button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
